/* eslint-disable react-refresh/only-export-components */
/**
 * ScenarioAttributesContext — DB 属性ツリー由来の選択肢群 (ほぼ不変)。
 *
 * `useScenarioAttributes()` (react-query, staleTime 1h) の結果からのみ導出されるため、
 * 初回フェッチ後は実質不変。旧・単一 Context ではこの約20メンバが settings や
 * selectedAssets と同じ value に同居していたため、無関係な更新のたびに
 * 消費者全体が再レンダーしていた。
 */
import { deriveShootingMethodOptions, LEGACY_SHOOTING_STYLES } from './shootingMethod';
import { deriveDialectOptions } from './dialect';
import { deriveMotionGraphicsOptions } from './motionGraphics';
import { deriveTargetAudienceOptions } from './targetAudience';
import { createContext, useContext, useMemo } from 'react';
import type { ReactNode } from 'react';

import type { ScenarioAttributeNode } from '../../../features/scenarios/api';
import { useScenarioAttributes } from '../../../features/scenarios/hooks';
import type { HierarchicalOption } from '../../common/HierarchicalSelect';
import type { BehaviorToggleOption } from './types';

export type ScenarioAttributesContextValue = {
  // 映像スタイルの DB 属性ツリー由来データ (フェーズ2)。treeEnabled が true のときフォームは
  // hierarchicalOptions を使い、選択中スタイルの stylized 判定は isStylized を使う。
  shootingMethodOptions: HierarchicalOption[];
  shootingMethodFieldPlacement: 'primary' | 'advanced';
  // シナリオ全体の方言。dialectVoiceAccents は option key → ナレーターの accent (API の生値)
  dialectOptions: HierarchicalOption[];
  dialectFieldPlacement: 'primary' | 'advanced';
  dialectVoiceAccents: Record<string, string>;
  // シナリオ全体の「モーショングラフィックス」3択の option (scenes / whole・計画 md §10.14)。
  // 空配列 = ツリーに field が無い (migration 未適用) → 新項目を出さず、全編は旧映像スタイルの metadata で判定する
  motionGraphicsOptions: HierarchicalOption[];
  motionGraphicsFieldPlacement: 'primary' | 'advanced';
  // シナリオ全体のターゲット性別・年代。空配列 = ツリーに field が無い (migration 未適用) → 項目を出さない
  targetGenderOptions: HierarchicalOption[];
  targetGenderFieldPlacement: 'primary' | 'advanced';
  targetAgeOptions: HierarchicalOption[];
  targetAgeFieldPlacement: 'primary' | 'advanced';
  visualStyleHierarchicalOptions: HierarchicalOption[];
  visualStyleTreeEnabled: boolean;
  // 映像スタイル「フィールド」全体の表示位置。primary=主表示の既定プルダウン /
  // advanced=詳細設定エキスパンド内。org override 反映済み。既定 advanced。
  visualStyleFieldPlacement: 'primary' | 'advanced';
  // トーンの DB 属性ツリー由来データ (フェーズ3)。treeEnabled が true のときフォームは
  // 旧 promptOptions ではなく toneTreeOptions を使う。
  toneTreeOptions: HierarchicalOption[];
  toneTreeEnabled: boolean;
  // 想定尺の DB 属性ツリー由来データ (フェーズ4)。flat {value(=key), label}。
  // metadata (seconds 等) も保持する。歌モノ (bgm_mode='lyrics') トーン選択時の
  // 30 秒未満 disable 判定に使う (docs/features/scenario-song-lyrics-bgm-plan.md §5.1.4)。
  durationTreeOptions: { value: string; label: string; metadata?: Record<string, unknown> | null }[];
  durationTreeEnabled: boolean;
  // 登場人物数の DB 属性ツリー由来データ。flat {value(=key), label}。duration と同形。
  characterCountTreeOptions: { value: string; label: string }[];
  characterCountTreeEnabled: boolean;
  // ナレ/セリフ/テロップの DB 属性ツリー由来データ (フェーズ6)。flat {value(=key), label, mode}。
  // treeEnabled が true のとき該当ドロップダウンは tree option を使い、state に option key を持つ。
  narrationTreeOptions: BehaviorToggleOption[];
  narrationTreeEnabled: boolean;
  narrationCategoryEnabled: boolean;
  dialogueTreeOptions: BehaviorToggleOption[];
  dialogueTreeEnabled: boolean;
  dialogueCategoryEnabled: boolean;
  telopTreeOptions: BehaviorToggleOption[];
  telopTreeEnabled: boolean;
  telopCategoryEnabled: boolean;
  // BGM テンプレート (docs/features/bgm-prompt-template-plan.md)。field 'bgm_template' 配下の
  // option を flat {value(=key), label} で持つ。本文 (prompt_text) は API が除去済みで FE には無い。
  // 空配列 = ツリーに無い (未投入 / OEM で非表示) → BGM セクションはセレクタ自体を出さない。
  // カテゴリ切替フラグ (categories_enabled) の対象外。
  bgmTemplateOptions: { value: string; label: string }[];
  // 属性ツリー取得が一度完了したか(成功/失敗問わず)。tree option の遅延ロードを待ってから
  // *_mode 復元するための readiness シグナル(フェーズ6)。
  scenarioAttributesLoaded: boolean;
};

/** 映像スタイル key → stylized 判定のマップ。公開 state ではなく SettingsProvider が
 *  `visualStyleIsStylized` を導出するためだけに使う内部値。 */
type ScenarioAttributesInternalValue = {
  visualStyleStylizedMap: Record<string, boolean>;
};

const ScenarioAttributesContext = createContext<ScenarioAttributesContextValue | null>(null);
const ScenarioAttributesInternalContext = createContext<ScenarioAttributesInternalValue | null>(null);

const EMPTY_STYLIZED_MAP: Record<string, boolean> = {};

/** 属性ツリーを key で深さ優先探索する (旧実装では各 useMemo 内に同じ関数が重複していた)。 */
function findAttributeNodeByKey(
  nodes: ScenarioAttributeNode[] | undefined,
  key: string,
): ScenarioAttributeNode | undefined {
  for (const n of nodes || []) {
    if (n.key === key) return n;
    const found = findAttributeNodeByKey(n.children, key);
    if (found) return found;
  }
  return undefined;
}

/**
 * field 'duration' 直下の option を {value, label, metadata} に変換する (純関数・テスト対象)。
 * metadata (seconds 等) は歌モノ (bgm_mode='lyrics') トーン選択時の 30 秒未満 disable 判定
 * (docs/features/scenario-song-lyrics-bgm-plan.md §5.1.4) に使う。
 */
export function deriveDurationTreeOptions(
  items: ScenarioAttributeNode[] | undefined,
): { value: string; label: string; metadata?: Record<string, unknown> | null }[] {
  const field = findAttributeNodeByKey(items, 'duration');
  if (!field?.children?.length) return [];
  return field.children
    .filter((n) => n.node_type === 'option')
    .map((n) => ({ value: n.key, label: n.label, metadata: n.metadata ?? null }));
}

/** field 'bgm_template' 直下の option を {value, label} に変換する (純関数・テスト対象)。 */
export function deriveBgmTemplateOptions(
  items: ScenarioAttributeNode[] | undefined,
): { value: string; label: string }[] {
  const field = findAttributeNodeByKey(items, 'bgm_template');
  if (!field?.children?.length) return [];
  return field.children
    .filter((n) => n.node_type === 'option')
    .map((n) => ({ value: n.key, label: n.label }));
}

export function ScenarioAttributesProvider({ children }: { children: ReactNode }) {
  // 映像スタイルを DB 属性ツリー由来に切り替える (フェーズ2)。categories_enabled に
  // 'visual_style' があり、ツリーに映像スタイル field-group があるときだけ DB 駆動にする。
  // 無ければフォームはハードコード選択肢にフォールバックする。
  const { data: scenarioAttributes, isFetched: scenarioAttributesLoaded } = useScenarioAttributes();
  const { visualStyleHierarchicalOptions, visualStyleTreeEnabled, visualStyleStylizedMap, visualStyleFieldPlacement } = useMemo(() => {
    // 既定は 'advanced'（現行どおり詳細設定エキスパンド内に表示）。
    const empty = {
      visualStyleHierarchicalOptions: [] as HierarchicalOption[],
      visualStyleTreeEnabled: false,
      visualStyleStylizedMap: {} as Record<string, boolean>,
      visualStyleFieldPlacement: 'advanced' as 'primary' | 'advanced',
    };
    const enabled = (scenarioAttributes?.categories_enabled || []).includes('visual_style');
    if (!enabled) return empty;
    const field = findAttributeNodeByKey(scenarioAttributes?.items, 'visual_style');
    if (!field?.children?.length) return empty;
    // フィールド全体の表示位置（主表示/詳細設定）。org override 適用済みの値。
    const fieldPlacement: 'primary' | 'advanced' = field.placement === 'primary' ? 'primary' : 'advanced';
    const stylizedMap: Record<string, boolean> = {};
    const toOption = (n: ScenarioAttributeNode): HierarchicalOption => {
      if (n.node_type === 'option') {
        const family = (n.metadata as { visual_family?: string } | null | undefined)?.visual_family;
        stylizedMap[n.key] = family === 'stylized';
      }
      return {
        id: n.id,
        key: n.key,
        label: n.label,
        type: n.node_type === 'group' ? 'group' : 'option',
        metadata: n.metadata ?? null,
        children: n.children?.map(toOption),
      };
    };
    const filterStyles = (nodes: ScenarioAttributeNode[]): ScenarioAttributeNode[] => nodes
      .filter(n => n.node_type !== 'option' || !LEGACY_SHOOTING_STYLES.has(n.key))
      .map(n => ({ ...n, children: n.children ? filterStyles(n.children) : undefined }))
      .filter(n => n.node_type !== 'group' || !!n.children?.length);
    const options = filterStyles(field.children).map(toOption);
    return {
      visualStyleHierarchicalOptions: options,
      visualStyleTreeEnabled: true,
      visualStyleStylizedMap: stylizedMap,
      visualStyleFieldPlacement: fieldPlacement,
    };
  }, [scenarioAttributes]);

  // トーン: 'tone' カテゴリが新ツリー経路に切替済みなら、属性ツリーの tone フィールドから
  // 階層オプションを構築する（旧 scenario_prompt_options 由来の構築を置き換える）。
  const { toneTreeOptions, toneTreeEnabled } = useMemo(() => {
    const empty = { toneTreeOptions: [] as HierarchicalOption[], toneTreeEnabled: false };
    const enabled = (scenarioAttributes?.categories_enabled || []).includes('tone');
    if (!enabled) return empty;
    const field = findAttributeNodeByKey(scenarioAttributes?.items, 'tone');
    if (!field?.children?.length) return empty;
    const toOption = (n: ScenarioAttributeNode): HierarchicalOption => ({
      id: n.id,
      key: n.key,
      label: n.label,
      type: n.node_type === 'group' ? 'group' : 'option',
      metadata: n.metadata ?? null,
      children: n.children?.map(toOption),
    });
    return { toneTreeOptions: field.children.map(toOption), toneTreeEnabled: true };
  }, [scenarioAttributes]);

  // 想定尺: 'duration' カテゴリが新ツリー経路に切替済みなら、属性ツリーの duration フィールドから
  // フラットな {value(=key), label} を構築する（旧 promptOptions.duration を置き換える）。
  const { durationTreeOptions, durationTreeEnabled } = useMemo(() => {
    const enabled = (scenarioAttributes?.categories_enabled || []).includes('duration');
    if (!enabled) return { durationTreeOptions: [], durationTreeEnabled: false };
    const field = findAttributeNodeByKey(scenarioAttributes?.items, 'duration');
    if (!field?.children?.length) return { durationTreeOptions: [], durationTreeEnabled: false };
    return { durationTreeOptions: deriveDurationTreeOptions(scenarioAttributes?.items), durationTreeEnabled: true };
  }, [scenarioAttributes]);

  // 登場人物数: 'character_count' カテゴリが新ツリー経路に切替済みなら、属性ツリーの
  // character_count フィールドからフラットな {value(=key), label} を構築する。duration と同形。
  const { characterCountTreeOptions, characterCountTreeEnabled } = useMemo(() => {
    const empty = { characterCountTreeOptions: [] as { value: string; label: string }[], characterCountTreeEnabled: false };
    const enabled = (scenarioAttributes?.categories_enabled || []).includes('character_count');
    if (!enabled) return empty;
    const field = findAttributeNodeByKey(scenarioAttributes?.items, 'character_count');
    if (!field?.children?.length) return empty;
    // character_count はグループ無し(flat)。option のみを拾う。
    const opts = field.children
      .filter((n) => n.node_type === 'option')
      .map((n) => ({ value: n.key, label: n.label }));
    return { characterCountTreeOptions: opts, characterCountTreeEnabled: true };
  }, [scenarioAttributes]);

  // ナレ/セリフ/テロップ(フェーズ6): behavior トグルの flat option を属性ツリーから構築する。
  // 各 option は { value(=key), label, mode } を持つ(mode は会話/後付け等の判定に使う)。
  // 「指定なし」はここに含めず、ドロップダウン側で先頭に合成する(DB ノードを作らないため)。
  const behaviorToggleTrees = useMemo(() => {
    const buildFor = (fieldKey: string) => {
      // categoryEnabled=フラグ ON。present=effective tree に field-group が存在(=自組織で
      // 非除外)。enabled(=tree UI を使う)は両方が真。flag ON かつ非存在(=backfill 等で
      // 除外)は「フラグ ON だが非表示」を表すため categoryEnabled だけ true にする。
      const categoryEnabled = (scenarioAttributes?.categories_enabled || []).includes(fieldKey);
      const empty = { options: [] as BehaviorToggleOption[], enabled: false, categoryEnabled };
      if (!categoryEnabled) return empty;
      const field = findAttributeNodeByKey(scenarioAttributes?.items, fieldKey);
      if (!field?.children?.length) return empty;
      const opts = field.children
        .filter((n) => n.node_type === 'option')
        .map((n) => ({
          value: n.key,
          label: n.label,
          mode: (n.metadata?.mode as string | undefined) ?? null,
        }));
      return opts.length ? { options: opts, enabled: true, categoryEnabled } : empty;
    };
    return {
      narration: buildFor('narration'),
      dialogue: buildFor('dialogue'),
      telop: buildFor('telop'),
    };
  }, [scenarioAttributes]);
  const narrationTreeOptions = behaviorToggleTrees.narration.options;
  const narrationTreeEnabled = behaviorToggleTrees.narration.enabled;
  const narrationCategoryEnabled = behaviorToggleTrees.narration.categoryEnabled;
  const dialogueTreeOptions = behaviorToggleTrees.dialogue.options;
  const dialogueTreeEnabled = behaviorToggleTrees.dialogue.enabled;
  const dialogueCategoryEnabled = behaviorToggleTrees.dialogue.categoryEnabled;
  const telopTreeOptions = behaviorToggleTrees.telop.options;
  const telopTreeEnabled = behaviorToggleTrees.telop.enabled;
  const telopCategoryEnabled = behaviorToggleTrees.telop.categoryEnabled;

  const bgmTemplateOptions = useMemo(
    () => deriveBgmTemplateOptions(scenarioAttributes?.items),
    [scenarioAttributes],
  );

  const { shootingMethodOptions, shootingMethodFieldPlacement } = useMemo(
    () => deriveShootingMethodOptions(scenarioAttributes?.items), [scenarioAttributes],
  );

  const { dialectOptions, dialectFieldPlacement, dialectVoiceAccents } = useMemo(
    () => deriveDialectOptions(scenarioAttributes?.items), [scenarioAttributes],
  );

  const { motionGraphicsOptions, motionGraphicsFieldPlacement } = useMemo(
    () => deriveMotionGraphicsOptions(scenarioAttributes?.items), [scenarioAttributes],
  );

  const { targetGenderOptions, targetGenderFieldPlacement, targetAgeOptions, targetAgeFieldPlacement } = useMemo(
    () => deriveTargetAudienceOptions(scenarioAttributes?.items), [scenarioAttributes],
  );

  const value = useMemo<ScenarioAttributesContextValue>(() => ({
    shootingMethodOptions, shootingMethodFieldPlacement,
    dialectOptions, dialectFieldPlacement, dialectVoiceAccents,
    motionGraphicsOptions, motionGraphicsFieldPlacement,
    targetGenderOptions, targetGenderFieldPlacement, targetAgeOptions, targetAgeFieldPlacement,
    visualStyleHierarchicalOptions,
    visualStyleTreeEnabled,
    visualStyleFieldPlacement,
    toneTreeOptions,
    toneTreeEnabled,
    durationTreeOptions,
    durationTreeEnabled,
    characterCountTreeOptions,
    characterCountTreeEnabled,
    narrationTreeOptions,
    narrationTreeEnabled,
    narrationCategoryEnabled,
    dialogueTreeOptions,
    dialogueTreeEnabled,
    dialogueCategoryEnabled,
    telopTreeOptions,
    telopTreeEnabled,
    telopCategoryEnabled,
    bgmTemplateOptions,
    scenarioAttributesLoaded,
  }), [
    shootingMethodOptions, shootingMethodFieldPlacement,
    dialectOptions, dialectFieldPlacement, dialectVoiceAccents,
    motionGraphicsOptions, motionGraphicsFieldPlacement,
    targetGenderOptions, targetGenderFieldPlacement, targetAgeOptions, targetAgeFieldPlacement,
    visualStyleHierarchicalOptions, visualStyleTreeEnabled, visualStyleFieldPlacement,
    toneTreeOptions, toneTreeEnabled, durationTreeOptions, durationTreeEnabled,
    characterCountTreeOptions, characterCountTreeEnabled,
    narrationTreeOptions, narrationTreeEnabled, narrationCategoryEnabled,
    dialogueTreeOptions, dialogueTreeEnabled, dialogueCategoryEnabled,
    telopTreeOptions, telopTreeEnabled, telopCategoryEnabled,
    bgmTemplateOptions,
    scenarioAttributesLoaded,
  ]);

  const internalValue = useMemo<ScenarioAttributesInternalValue>(
    () => ({ visualStyleStylizedMap }),
    [visualStyleStylizedMap],
  );

  return (
    <ScenarioAttributesContext.Provider value={value}>
      <ScenarioAttributesInternalContext.Provider value={internalValue}>
        {children}
      </ScenarioAttributesInternalContext.Provider>
    </ScenarioAttributesContext.Provider>
  );
}

/** 属性ツリー由来の選択肢だけを購読する。settings / assets の更新では再レンダーしない。 */
export function useScenarioAttributesState(): ScenarioAttributesContextValue {
  const ctx = useContext(ScenarioAttributesContext);
  if (!ctx) {
    throw new Error('useScenarioAttributesState must be used within GenerationProvider');
  }
  return ctx;
}

/** Provider 外では null を返す版 (合成ファサード用)。 */
export function useScenarioAttributesStateSafe(): ScenarioAttributesContextValue | null {
  return useContext(ScenarioAttributesContext);
}

/** SettingsProvider が visualStyleIsStylized を導出するための内部フック。 */
export function useVisualStyleStylizedMap(): Record<string, boolean> {
  return useContext(ScenarioAttributesInternalContext)?.visualStyleStylizedMap ?? EMPTY_STYLIZED_MAP;
}
